import type { TribuNotification } from '../../api-spec/lib';
import { RecruiterPostulationItemDto } from '../../api-spec/lib';

export enum ActionsOnReceive {
  IDENTITY_VERIFICATION_UPDATE = 'identity-verification.update',
  PROFILE_UPDATE = 'profile.update',
  CREDITS_UPDATE = 'credits.update',
  INVITATIONS_UPDATE = 'invitations.update',
  APPOINTMENT_UPDATE = 'appointment.update',
  SESSIONS_UPDATE = 'sessions.update',
  RESERVATIONS_UPDATE = 'reservations.update',
  JOBS_UPDATE = 'jobs.update',
  MY_JOBS_UPDATE = 'my-jobs.update',
  POSTULATIONS_UPDATE = 'postulations.update',
  VIDEO_INTERVIEW_REQUEST_COMPLETED = 'video-interview-request.completed',
  VIDEO_INTERVIEW_REQUEST_FAILED = 'video-interview-request.failed',
  VIDEO_INTERVIEW_PROGRESS = 'video-interview-request.progress',
  VIDEO_CALL_INVITATIONS_UPDATE = 'video-call-invitations.update',
  NOTIFICATION_CREATED = 'notification.created',
  CV_SCREENING_DOCUMENT_UPDATED = 'cv-screening.document-updated',
  CV_SCREENING_JOB_READY = 'cv-screening.job-ready',
  CV_SCREENING_CANDIDATE_CV = 'cv-screening.candidate-cv',
}

export type IdentityVerificationStatus = 'APPROVED' | 'DECLINED';

export interface PostulationsUpdatePayload {
  userId: string;
  jobUid?: string;
  postulationUid?: string;
  status?: RecruiterPostulationItemDto.StatusEnum;
}

export interface NotificationCreatedData {
  userId: string;
  notification: TribuNotification;
}

export interface VideoInterviewRequestCompletedData {
  userId: string;
  requestId: string;
  resultUrl: string;
  videoaskUrl: string | null;
  videoaskPreviewUrl: string | null;
  jobUid?: string;
}

export interface VideoInterviewRequestFailedData {
  userId: string;
  requestId: string;
  jobUid?: string;
}

/**
 * Lane 3 fine-grained progress (DEV-172). Emitted once per question video the worker
 * finalizes in GCS. The front derives `completed/N` from the SET of distinct
 * `questionId`s (monotonic) — `questionId` is the uuid, NOT an ordinal index.
 */
export interface VideoInterviewProgressData {
  userId: string;
  requestId: string;
  questionId: string;
}

export interface VideoCallInvitationsUpdateData {
  userId: string;
  videoCallInvitationId: string;
  jobUid: string;
}

/** The outcomes a screened CV ends with; the values of `CvDocumentStatus` a push can carry. */
export const CV_SCREENING_DOCUMENT_OUTCOME = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  REJECTED: 'REJECTED',
  DUPLICATE: 'DUPLICATE',
} as const;

export type CvScreeningDocumentOutcome =
  (typeof CV_SCREENING_DOCUMENT_OUTCOME)[keyof typeof CV_SCREENING_DOCUMENT_OUTCOME];

/**
 * CV screening (design.md D-46): the worker emits one per CV it resolves, after the terminal write.
 * Identifiers only: the front refreshes the row from `GET /cv-screening/jobs/:jobId/documents`.
 */
export interface CvScreeningDocumentUpdatedData {
  userId: string;
  jobUid: string;
  documentUid: string;
  status: CvScreeningDocumentOutcome;
}

/** CV screening (D-46): the list of a vacancy is available — none of its CVs is still in process. */
export interface CvScreeningJobReadyData {
  userId: string;
  jobUid: string;
}

/** A CV sent through the public form finished processing; identifiers only (public-cv-intake CSP-001). */
export interface CvScreeningCandidateCvData {
  userId: string;
  jobUid: string;
  documentUid: string;
}

export type NotificationPayload =
  | {
      type: ActionsOnReceive.IDENTITY_VERIFICATION_UPDATE;
      data: { userId: string; status: IdentityVerificationStatus };
    }
  | {
      type: ActionsOnReceive.PROFILE_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.CREDITS_UPDATE;
      data: { userId: string; balance?: number };
    }
  | {
      type: ActionsOnReceive.INVITATIONS_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.APPOINTMENT_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.SESSIONS_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.RESERVATIONS_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.JOBS_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.MY_JOBS_UPDATE;
      data: { userId: string };
    }
  | {
      type: ActionsOnReceive.POSTULATIONS_UPDATE;
      data: PostulationsUpdatePayload;
    }
  | {
      type: ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_COMPLETED;
      data: VideoInterviewRequestCompletedData;
    }
  | {
      type: ActionsOnReceive.VIDEO_INTERVIEW_REQUEST_FAILED;
      data: VideoInterviewRequestFailedData;
    }
  | {
      type: ActionsOnReceive.VIDEO_INTERVIEW_PROGRESS;
      data: VideoInterviewProgressData;
    }
  | {
      type: ActionsOnReceive.VIDEO_CALL_INVITATIONS_UPDATE;
      data: VideoCallInvitationsUpdateData;
    }
  | {
      type: ActionsOnReceive.NOTIFICATION_CREATED;
      data: NotificationCreatedData;
    }
  | {
      type: ActionsOnReceive.CV_SCREENING_DOCUMENT_UPDATED;
      data: CvScreeningDocumentUpdatedData;
    }
  | {
      type: ActionsOnReceive.CV_SCREENING_JOB_READY;
      data: CvScreeningJobReadyData;
    }
  | {
      type: ActionsOnReceive.CV_SCREENING_CANDIDATE_CV;
      data: CvScreeningCandidateCvData;
    };

export interface PushEventEnvelope {
  userId: string;
  payload: NotificationPayload;
  actionsOnReceive: ActionsOnReceive[];
  emittedAt: number;
}
